/**
 * The town a listing is in — for the result card's "Göteborg · ca 12 km"
 * (P25009-425 BR-3): a town, never a postcode, a street or an address.
 *
 * `publicData.city` (and `publicData.location.city`) were written from the
 * Mapbox suggestion the seller picked — its own `text` — which is the town
 * only when the pick WAS a town. Picking a street address stored the street
 * ("Exportgatan"), picking a postcode stored the postcode ("13220", the card
 * bug the ticket names). The place name Mapbox returned with it
 * (`location.address`, "Exportgatan 12, 422 46 Hisings Backa, Sverige") does
 * hold the town, after the postcode, so that is read first. New saves take the
 * town from the suggestion's context instead (townFromMapboxFeature).
 *
 * CommonJS, so the server reads towns the same way (the listing writers that
 * place a listing at the seller's address, P25009-425 follow-up).
 */

/* "422 46 Hisings Backa" / "10115 Berlin": a five-digit postcode, then the town. */
const TOWN_AFTER_POSTCODE = /(?:^|[\s,])\d{3}\s?\d{2}\s+([^\d,][^,]*)/;

const clean = value => (typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '');

const partsOf = placeName =>
  clean(placeName)
    .split(',')
    .map(clean)
    .filter(Boolean);

/**
 * True when `value` can be shown as a town: words, no digits, not the street
 * of the address it came with ("Exportgatan" next to "Exportgatan 12, …") and
 * not its country ("Sverige" when the seller picked the whole country).
 */
const looksLikeTown = (value, address = '') => {
  const town = clean(value);
  if (town.length < 2 || /\d/.test(town)) return false;
  const parts = partsOf(address);
  const firstPart = parts[0] || '';
  const isStreetOfAddress =
    /\d/.test(firstPart) && firstPart.toLowerCase().startsWith(town.toLowerCase());
  const isCountryOfAddress = parts.length > 0 && town.toLowerCase() === parts[parts.length - 1].toLowerCase();
  return !isStreetOfAddress && !isCountryOfAddress;
};

/**
 * The town in a Mapbox place name. Swedish names come in two shapes:
 *   "Hagagatan 11, 332 35 Gislaved, Sverige"           — after the postcode
 *   "Sisjön, Mölndal, Västra Götalands län 431 90, …"  — before the county
 *   "Göteborg, Västra Götalands län, Sverige"          — (a town picked as such)
 */
const townFromPlaceName = placeName => {
  const match = clean(placeName).match(TOWN_AFTER_POSTCODE);
  const afterPostcode = match ? clean(match[1]) : '';
  if (looksLikeTown(afterPostcode)) return afterPostcode;
  const parts = partsOf(placeName);
  const countyIndex = parts.findIndex(part => /\blän\b/i.test(part));
  const beforeCounty = countyIndex > 0 ? parts[countyIndex - 1] : '';
  return looksLikeTown(beforeCounty, placeName) ? beforeCounty : null;
};

/**
 * The town of a Mapbox geocoding feature: the feature itself when it is a
 * town, else the town (or, failing that, the locality) in its context.
 * Undefined for a feature that names none — the browser's "current location"
 * suggestion among them.
 */
const townFromMapboxFeature = feature => {
  if (!feature || typeof feature !== 'object') return undefined;
  const types = Array.isArray(feature.place_type) ? feature.place_type : [];
  if (types.includes('place') && looksLikeTown(feature.text)) return clean(feature.text);
  const context = Array.isArray(feature.context) ? feature.context : [];
  const ofType = type => context.find(c => String(c?.id || '').startsWith(`${type}.`));
  const town = ofType('place') || ofType('locality');
  if (town && looksLikeTown(town.text)) return clean(town.text);
  if (types.includes('locality') && looksLikeTown(feature.text)) return clean(feature.text);
  return townFromPlaceName(feature.place_name) || undefined;
};

/* The county (Län) field's label for its stored key ("VastraGotaland" ->
 * "Västra Götaland"). */
const countyLabel = (county, listingFields) => {
  if (!county) return null;
  const field = (listingFields || []).find(f => f?.key === 'county');
  const option = field?.enumOptions?.find(o => o?.option === county);
  if (option?.label) return option.label;
  /* Without a label, a plain word ("Halland") can stand; a camel-case key
   * ("VastraGotaland") cannot. */
  return looksLikeTown(county) && !/[a-zåäö][A-ZÅÄÖ]/.test(county) ? county : null;
};

/**
 * The listing's town, or null: the town after the postcode in the stored
 * place name, else the stored city when it reads as a town.
 *
 * @param {Object} publicData the listing's publicData
 */
const listingTown = publicData => {
  const pd = publicData || {};
  const location = pd.location && typeof pd.location === 'object' ? pd.location : {};
  const address = location.address || '';
  const candidates = [townFromPlaceName(address), pd.city, location.city];
  const town = candidates.find(value => looksLikeTown(value, address));
  return town ? clean(town) : null;
};

/**
 * The place to show on a result card: the town (listingTown), else the
 * county's name — a region, but never a raw key or a number.
 *
 * @param {Object} publicData the listing's publicData
 * @param {Array} [listingFields] hosted listing fields, for the county label
 */
const listingTownName = (publicData, listingFields) =>
  listingTown(publicData) || countyLabel(publicData?.county, listingFields);

module.exports = {
  looksLikeTown,
  townFromPlaceName,
  townFromMapboxFeature,
  listingTown,
  listingTownName,
};
